import { useEffect, useState } from "react";
import { getLastParsingDate } from "../api";

export const LAST_PARSING_DATE_LABEL = "\u0414\u0430\u0442\u0430 \u043e\u0431\u043d\u043e\u0432\u043b\u0435\u043d\u0438\u044f \u0434\u0430\u043d\u043d\u044b\u0445";

export type LastParsingDateInfo = {
  display: string | null;
  date: string | null;
  raw: string | null;
  loading: boolean;
  groupId: number | null;
  daysStay: number;
};

const emptyLastParsingDate = (daysStay: number): LastParsingDateInfo => ({
  display: null,
  date: null,
  raw: null,
  loading: false,
  groupId: null,
  daysStay,
});

function formatLastParsingDate(value: string) {
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!match) return value;
    return `${match[3]}.${match[2]}.${match[1]} ${match[4]}:${match[5]}`;
  }

  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getLastParsingDateOnly(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;

  return date.toISOString().slice(0, 10);
}

export function isLastParsingDateInfoReady(
  info: LastParsingDateInfo,
  groupId: number | null,
  daysStay = 1,
  enabled = true,
) {
  if (!groupId || !enabled) return true;
  return !info.loading && info.groupId === groupId && info.daysStay === daysStay;
}

export function useLastParsingDateInfo(groupId: number | null, daysStay = 1, enabled = true) {
  const [lastParsingDate, setLastParsingDate] = useState<LastParsingDateInfo>(() => emptyLastParsingDate(daysStay));

  useEffect(() => {
    if (!groupId || !enabled) {
      setLastParsingDate(emptyLastParsingDate(daysStay));
      return;
    }

    let isMounted = true;
    const payload = {
      group_id: groupId,
      days_stay: daysStay,
    };

    setLastParsingDate({
      display: null,
      date: null,
      raw: null,
      loading: true,
      groupId: null,
      daysStay,
    });

    getLastParsingDate(payload)
      .then((response) => {
        if (!isMounted) return;
        setLastParsingDate({
          display: response.date ? formatLastParsingDate(response.date) : null,
          date: response.date ? getLastParsingDateOnly(response.date) : null,
          raw: response.date || null,
          loading: false,
          groupId,
          daysStay,
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setLastParsingDate({
          ...emptyLastParsingDate(daysStay),
          groupId,
        });
      });

    return () => {
      isMounted = false;
    };
  }, [daysStay, enabled, groupId]);

  return lastParsingDate;
}

export function useLastParsingDate(groupId: number | null, daysStay = 1, enabled = true) {
  return useLastParsingDateInfo(groupId, daysStay, enabled).display;
}
